import { computed, reactive, ref } from 'vue'
import type { FinancingRegime } from '@/api/generated-organizations'
import { useOrganizationStore } from '@/stores/organization'

/**
 * The regimes Organisatiebeheer › Bekostiging offers a switch for (#4697), in display order.
 * PARTICULIER, PGB and ANDERS are not regimes a practice sets up and are left out.
 */
export const OFFERED_FINANCING_REGIMES = ['ZVW', 'JEUGDWET', 'WMO'] as const satisfies readonly FinancingRegime[]

export type OfferedFinancingRegime = (typeof OFFERED_FINANCING_REGIMES)[number]

/**
 * Zvw is shown as on and cannot be switched off. Every existing organisation is Zvw-only while
 * `enabledFinancingRegimes` defaults to empty, so reading the stored set literally would hide a
 * live practice's whole billing — and offering the switch would invite exactly that change.
 */
export const ALWAYS_ENABLED_REGIME: FinancingRegime = 'ZVW'

/**
 * Which financing regimes the signed-in organisation works under (ADR-030) — the one place the
 * frontend asks.
 *
 * Enablement decides what an organisation *can* set up: the gemeente contract type, and the
 * Jeugdwet surfaces. What appears in a single dossier is a separate question, answered by the
 * record itself (#2757).
 *
 * Reads the organisation already in the store and writes through the ordinary organisation
 * update, behind the permission that update already requires (`organization.settings.update`).
 */
export function useFinancingRegimes() {
  const organizationStore = useOrganizationStore()

  /** A switch that is mid-save shows what was asked for, not what is stored. */
  const pending = reactive(new Map<FinancingRegime, boolean>())
  const saving = ref(false)

  const storedRegimes = computed(
    () => new Set<FinancingRegime>(organizationStore.organization?.enabledFinancingRegimes ?? [])
  )

  function isRegimeEnabled(regime: FinancingRegime): boolean {
    return pending.get(regime) ?? (regime === ALWAYS_ENABLED_REGIME || storedRegimes.value.has(regime))
  }

  /** A gemeente contract is how both Jeugdwet and Wmo care is contracted. */
  const offersGemeenteContract = computed(() => isRegimeEnabled('JEUGDWET') || isRegimeEnabled('WMO'))

  /** Loads the organisation if nothing has yet; a failure leaves every optional regime off. */
  async function ensureLoaded(): Promise<void> {
    await organizationStore.ensureLoaded().catch(() => undefined)
  }

  /**
   * Switches one regime on or off and persists it. Zvw is written along with the rest, so what
   * is stored agrees with what the screen says. A failure is logged and surfaced by the store,
   * as for any other organisation update, and the switch falls back to what is stored.
   *
   * @returns whether the change was saved.
   */
  async function setRegimeEnabled(regime: FinancingRegime, enabled: boolean): Promise<boolean> {
    if (regime === ALWAYS_ENABLED_REGIME) return false

    const next = new Set(storedRegimes.value)
    next.add(ALWAYS_ENABLED_REGIME)
    if (enabled) next.add(regime)
    else next.delete(regime)

    pending.set(regime, enabled)
    saving.value = true
    try {
      await organizationStore.updateOrganization({ enabledFinancingRegimes: [...next] })
      return true
    } catch {
      return false
    } finally {
      pending.delete(regime)
      saving.value = false
    }
  }

  return { isRegimeEnabled, offersGemeenteContract, ensureLoaded, setRegimeEnabled, saving }
}
